<template>
  <div class="mt-4">
    <div class="d-flex align-center mb-1">
      <span class="subtitle-2">{{ $t('crudUnidades.locations.title') }}</span>
      <v-chip v-if="!loading" x-small class="ml-2">{{ locations.length }}</v-chip>
      <v-spacer />
      <v-btn v-if="canInsert" text small color="primary" class="px-0" @click="abrirInclusao">
        + {{ $t('crudUnidades.locations.add') }}
      </v-btn>
    </div>
    <div class="caption text--secondary mb-2">{{ $t('crudUnidades.locations.hint') }}</div>
    <v-data-table
      :headers="headers"
      :items="linhas"
      item-key="id_location"
      :loading="loading"
      :loading-text="$t('system.messages.loading')"
      :no-data-text="$t('crudUnidades.locations.empty')"
      :items-per-page="5"
      :hide-default-footer="linhas.length <= 5"
      :footer-props="{ 'items-per-page-options': [5, 10, -1] }"
      dense
      class="elevation-0 unidade-locations-table"
    >
      <template v-slot:item.cod_location="{ item }">
        <span>{{ item.cod_location }}</span>
        <v-chip v-if="item.origem === 'MANUAL'" x-small label outlined class="ml-1">
          {{ $t('crudUnidades.locations.manual') }}
        </v-chip>
        <v-tooltip v-if="item.bloqueada" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon x-small class="ml-1" v-bind="attrs" v-on="on">mdi-lock</v-icon>
          </template>
          <span>{{ $t('crudUnidades.locations.lockedHint') }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.ativa="{ item }">
        <v-chip x-small label :color="item.ativa ? 'success' : 'grey'" text-color="white">
          {{ item.ativa ? $t('crudUnidades.locations.active') : $t('crudUnidades.locations.inactive') }}
        </v-chip>
      </template>
      <template v-slot:item.dt_atualizacao="{ item }">
        <div>{{ item.dt_atualizacao }}</div>
        <div class="caption text--secondary">
          {{ item.autor ? $t('crudUnidades.locations.by', { name: item.autor }) : $t('crudUnidades.locations.bySync') }}
        </div>
      </template>
      <template v-slot:item.acoes="{ item }">
        <div class="d-flex justify-end">
          <v-btn v-if="canEdit" icon x-small :title="$t('button.edit')" @click="abrirEdicao(item)">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            v-if="canEdit && item.bloqueada"
            icon
            x-small
            :title="$t('crudUnidades.locations.unlock')"
            @click="confirmar('destravar', item)"
          >
            <v-icon small>mdi-lock-open-variant</v-icon>
          </v-btn>
          <v-btn
            v-if="canToggle"
            icon
            x-small
            :title="item.ativa ? $t('crudUnidades.locations.deactivate') : $t('crudUnidades.locations.reactivate')"
            @click="confirmar(item.ativa ? 'desativar' : 'reativar', item)"
          >
            <v-icon small :color="item.ativa ? 'red' : 'success'">
              {{ item.ativa ? 'mdi-cancel' : 'mdi-restore' }}
            </v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <v-dialog v-model="dialogForm" max-width="480" persistent>
      <v-card>
        <v-card-title class="subtitle-1">
          {{ editando ? $t('crudUnidades.locations.editTitle') : $t('crudUnidades.locations.addTitle') }}
        </v-card-title>
        <v-card-text>
          <v-alert v-if="editando && editando.origem !== 'MANUAL'" type="info" dense text class="caption">
            {{ $t('crudUnidades.locations.ssffEditWarning') }}
          </v-alert>
          <v-form ref="form" v-model="formValido" @submit.prevent="salvar">
            <v-text-field
              v-model="form.cod_location"
              :label="$t('crudUnidades.locations.headers.code')"
              :rules="[regraCodigo]"
              :disabled="!!editando && editando.origem !== 'MANUAL'"
              counter="20"
              dense
              outlined
            />
            <v-text-field
              v-model="form.nome_location"
              :label="$t('crudUnidades.locations.headers.name')"
              counter="255"
              dense
              outlined
            />
            <v-text-field
              v-model="form.cod_pais"
              :label="$t('crudUnidades.locations.headers.country')"
              :hint="$t('crudUnidades.locations.countryHint')"
              persistent-hint
              counter="10"
              dense
              outlined
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="salvando" @click="dialogForm = false">{{ $t('button.cancel') }}</v-btn>
          <v-btn color="primary darken-1" :loading="salvando" :disabled="!formValido" @click="salvar">
            {{ $t('button.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogConfirmacao" max-width="420">
      <v-card>
        <v-card-title class="subtitle-1">{{ confirmacao.titulo }}</v-card-title>
        <v-card-text>{{ confirmacao.mensagem }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="salvando" @click="dialogConfirmacao = false">{{ $t('button.cancel') }}</v-btn>
          <v-btn color="primary darken-1" :loading="salvando" @click="executarConfirmacao">
            {{ $t('button.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import apiController from '@/utils/apiController';
import EventBus from '@/utils/eventBus';

/**
 * Locations da unidade. As do SSFF são mantidas pela rotina de locations; pela tela é
 * possível criar locations manuais, editar e desativar/reativar. Editar ou desativar uma
 * location do SSFF a trava para a rotina até ser destravada aqui. Cada ação fica no
 * histórico (historico_unidades_locations_eventos) com o usuário e a data.
 */
export default {
  name: 'UnidadeLocations',
  props: {
    idUnidade: { type: [Number, String], default: null },
    locations: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    canInsert: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: false },
    canToggle: { type: Boolean, default: false },
  },
  data: () => ({
    dialogForm: false,
    dialogConfirmacao: false,
    formValido: true,
    salvando: false,
    editando: null,
    form: { cod_location: '', nome_location: '', cod_pais: '' },
    confirmacao: { acao: null, item: null, titulo: '', mensagem: '' },
  }),
  computed: {
    temAcoes() {
      return this.canEdit || this.canToggle;
    },
    headers() {
      const headers = [
        { text: this.$t('crudUnidades.locations.headers.code'), value: 'cod_location' },
        { text: this.$t('crudUnidades.locations.headers.name'), value: 'nome_location' },
        { text: this.$t('crudUnidades.locations.headers.country'), value: 'cod_pais' },
        { text: this.$t('crudUnidades.locations.headers.status'), value: 'ativa' },
        { text: this.$t('crudUnidades.locations.headers.updatedAt'), value: 'dt_atualizacao', sortable: false },
      ];
      if (this.temAcoes) {
        headers.push({ text: '', value: 'acoes', sortable: false, align: 'end' });
      }
      return headers;
    },
    linhas() {
      return (this.locations || []).map((l) => {
        const atualizada = !!l.dt_ultima_atualizacao;
        return {
          ...l,
          ativa: Number(l.ativa) === 1,
          bloqueada: Number(l.bloqueada_sync) === 1,
          dt_atualizacao: atualizada ? l.dt_ultima_atualizacao : l.dt_inclusao,
          autor: atualizada ? l.nome_usuario_atualizacao : l.nome_usuario_inclusao,
        };
      });
    },
  },
  methods: {
    regraCodigo(v) {
      const cod = (v || '').trim();
      if (!cod) return this.$t('crudUnidades.locations.codeRequired');
      if (cod.length > 20) return this.$t('crudUnidades.locations.codeTooLong');
      return true;
    },
    abrirInclusao() {
      this.editando = null;
      this.form = { cod_location: '', nome_location: '', cod_pais: '' };
      this.dialogForm = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    abrirEdicao(item) {
      this.editando = item;
      this.form = {
        cod_location: item.cod_location || '',
        nome_location: item.nome_location || '',
        cod_pais: item.cod_pais || '',
      };
      this.dialogForm = true;
    },
    salvar() {
      if (!this.$refs.form.validate()) return;
      const dados = {
        cod_location: this.form.cod_location.trim(),
        nome_location: this.form.nome_location,
        cod_pais: this.form.cod_pais,
      };
      this.salvando = true;
      const request = this.editando
        ? apiController.request(apiController.endpoints.updateLocationunidade, {
          data: { ...dados, id_location: this.editando.id_location },
        })
        : apiController.request(apiController.endpoints.insertLocationunidade, {
          data: { ...dados, id_unidade: Number(this.idUnidade) },
        });
      this.tratarResposta(request).then((ok) => {
        if (ok) this.dialogForm = false;
      });
    },
    confirmar(acao, item) {
      const chaves = {
        desativar: ['deactivate', 'deactivateConfirm'],
        reativar: ['reactivate', 'reactivateConfirm'],
        destravar: ['unlock', 'unlockConfirm'],
      }[acao];
      this.confirmacao = {
        acao,
        item,
        titulo: this.$t(`crudUnidades.locations.${chaves[0]}`),
        mensagem: this.$t(`crudUnidades.locations.${chaves[1]}`, { code: item.cod_location }),
      };
      this.dialogConfirmacao = true;
    },
    executarConfirmacao() {
      const { acao, item } = this.confirmacao;
      this.salvando = true;
      const request = acao === 'destravar'
        ? apiController.request(apiController.endpoints.updateLocationunidade, {
          data: { id_location: item.id_location, destravar: true },
        })
        : apiController.request(apiController.endpoints.deleteLocationunidade, {
          data: { id_location: item.id_location, ativa: acao === 'reativar' ? 1 : 0 },
        });
      this.tratarResposta(request).then((ok) => {
        if (ok) this.dialogConfirmacao = false;
      });
    },
    /** Erros já são exibidos pelo apiController; aqui só o aviso de sucesso e o recarregamento. */
    tratarResposta(request) {
      return request
        .then((response) => {
          EventBus.$emit('show-alert', { mensagem: response.data.mensagem, tipo: 'success' });
          this.$emit('changed');
          return true;
        })
        .catch(() => false)
        .finally(() => {
          this.salvando = false;
        });
    },
  },
};
</script>

<style scoped>
.unidade-locations-table {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
}
</style>
